import React, { useMemo, useState } from 'react';
import { MdClose, MdUnfoldLess, MdUnfoldMore } from 'react-icons/md';
import { Dropdown } from 'lib/components';
import { Divider } from 'app/pages/admin/index.styled';
import { components } from 'react-select';
import { BiChevronDown } from 'react-icons/bi';
import { DropdownLabelWrapper, LabelText } from 'lib/components/folders/style';
import {
  ParagraphNormal,
  ParagraphNormal14,
} from 'lib/components/styles/typography';
import {
  FolderAccessSettings,
  LabelOption,
  OemCardItem,
  RowData,
} from './types';
import { ItemContainer, ItemDetails } from './style';
import { useTheme } from 'styled-components/macro';
import { Flex } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { isMatchTarget, sanitizeText } from 'lib/utils/functions';

export type Props = {
  item: OemCardItem;
  accessOptionsData: FolderAccessSettings[];
  isLastItem?: boolean;
  setAccessData: React.Dispatch<React.SetStateAction<FolderAccessSettings[]>>;
};

export const OemCardManageAccess = ({
  item,
  accessOptionsData,
  isLastItem,
  setAccessData,
}: Props) => {
  const [showMore, setShowMore] = useState<boolean>(false);

  const themes = useTheme();
  const isToggleAllowed = (item?.rowData?.length ?? 0) > 0;

  const handleToggle: () => void = () => {
    if (isToggleAllowed) {
      setShowMore(prev => !prev);
    }
  };

  const DropdownIndicator = (props: any) => {
    if (!!item.isReadOnly) {
      return <></>;
    }
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <BiChevronDown />
        </components.DropdownIndicator>
      )
    );
  };

  const dropdownAccessData = useMemo(() => {
    const isReadOnly = item?.isReadOnly === true;

    return accessOptionsData.map((item: FolderAccessSettings) => ({
      value: item.id?.toString(),
      label: (
        <DropdownLabelWrapper>
          <LabelText
            color={
              isReadOnly ? themes.colors.neutral[40] : themes.colors.black[100]
            }
          >
            {sanitizeText(String(item.name))}
          </LabelText>
        </DropdownLabelWrapper>
      ),
    }));
  }, [accessOptionsData]);

  const handleOnChange = (value: LabelOption, rowData: RowData) => {
    setAccessData((prev: FolderAccessSettings[]) =>
      prev.map((data: FolderAccessSettings) => {
        return isMatchTarget(data, item, rowData)
          ? { ...data, access: value.value }
          : data;
      })
    );
  };

  const handleRemoveItem = (rowData: RowData) => {
    setAccessData((prev: FolderAccessSettings[]) =>
      prev.map((data: FolderAccessSettings) =>
        isMatchTarget(data, item, rowData) ? { ...data, isDeleted: true } : data
      )
    );
  };

  return (
    <>
      <ItemContainer>
        <Flex
          flexDirection='row'
          backgroundColor={themes.colors.white[100]}
          height='36px'
          alignItems='center'
          justifyContent='space-between'
          padding='16px 6px 16px 16px'
        >
          <Flex
            cursor={isToggleAllowed ? 'pointer' : 'default'}
            alignItems='center'
            gap='8px'
            onClick={handleToggle}
            flexDirection='row'
          >
            {showMore ? (
              <MdUnfoldLess fontSize={24} color={themes.colors.secondary[60]} />
            ) : (
              <MdUnfoldMore fontSize={24} color={themes.colors.secondary[60]} />
            )}
            <ParagraphNormal>{sanitizeText(item.name)}</ParagraphNormal>
          </Flex>
        </Flex>
        {(!isLastItem || (!!isLastItem && showMore)) && (
          <Divider marginTop='2px' maxWidth='798px' marginBottom='2px' />
        )}
        <ItemDetails show={showMore}>
          {item.rowData?.map(row => {
            const selected =
              dropdownAccessData.find(o => o.value === String(row.access)) ??
              null;

            const rowKey =
              'targetRegionId' in row
                ? `${row.targetRegionId}-${row.targetOrganizationId}`
                : `${row.targetCustomerId}-${row.targetOrganizationId}`;

            return (
              <Flex
                key={rowKey}
                flexDirection='row'
                backgroundColor={themes.colors.white[100]}
                height='40px'
                alignItems='center'
                justifyContent='space-between'
              >
                <Flex flexDirection='row' gap='17px' width='50%'>
                  <ParagraphNormal>
                    {sanitizeText(
                      String(
                        'targetRegionId' in row
                          ? row.targetRegionName
                          : row.targetCustomerName
                      )
                    )}
                  </ParagraphNormal>
                  <ParagraphNormal14 color={themes.colors.neutral[40]}>
                    {' '}
                    {sanitizeText(String(row.targetOrganizationName))}
                  </ParagraphNormal14>
                </Flex>
                <Flex
                  alignItems='center'
                  gap='8px'
                  flexDirection='row'
                  width=''
                >
                  <Dropdown
                    menuPortalTarget={document.body}
                    disabled={!!item.isReadOnly}
                    extendStyles={{
                      menuPortal: {
                        zIndex: 9999,
                      },
                      control: {
                        borderStyle: 'hidden',
                        backgroundColor: themes.colors.white[100],
                      },
                      valueContainer: {
                        padding: 0,
                        marginRight: '-10px',
                      },
                      menu: {
                        width: '170px',
                        right: '0px',
                        zIndex: 9999,
                      },
                      container: {
                        margin: '0 20px 0 0',
                      },
                    }}
                    customComponents={{
                      IndicatorSeparator: () => null,
                      DropdownIndicator: DropdownIndicator,
                    }}
                    height={40}
                    creatable={false}
                    className='dropdown'
                    placeholder={'Select Option'}
                    zIndexProp={1}
                    value={selected}
                    onChange={value => handleOnChange(value, row)}
                    options={dropdownAccessData}
                  />
                  {!item.isReadOnly && (
                    <Button
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: themes.colors.black[100],
                      }}
                      icon={<MdClose size='18' />}
                      onClick={() => {
                        handleRemoveItem(row);
                      }}
                    />
                  )}
                </Flex>
              </Flex>
            );
          })}
        </ItemDetails>

        {!!showMore && (
          <Divider marginTop='2px' maxWidth='798px' marginBottom='2px' />
        )}
      </ItemContainer>
    </>
  );
};
